// LEGO filters entities with no item assigned for a collection type on the URL-encoded NULL byte.
const NULL_FILTER_VALUE = '%00';

const toArray = value => (Array.isArray(value) ? value : []);

const hasCollectionItems = collection => Boolean(collection?.type) && Array.isArray(collection.collection_item_ids) && collection.collection_item_ids.length > 0;

const usableCollections = (collections = []) => toArray(collections).filter(hasCollectionItems);

const isUnassignedItemId = id => id === null || id === undefined;

const toQueryValue = id => (isUnassignedItemId(id) ? NULL_FILTER_VALUE : encodeURIComponent(id));

const formatCollectionQueryParam = (type, id) => `collections.${type}=${toQueryValue(id)}`;

/**
 * Normalizes a raw `collections` array (as received from the host over the
 * GET_ACCESS channel) into the shape LEGO's phase-1 contract expects: each
 * type may carry at most one collection_item_id.
 */
export const normalizeCollections = (collections = []) =>
  usableCollections(collections).map(collection => ({
    type: collection.type,
    collection_item_ids: collection.collection_item_ids.slice(0, 1),
  }));

/**
 * Builds the `collections` block for entity POST/PUT request bodies.
 * Returns an empty object (no `collections` key) when there is nothing to send,
 * so it can be safely spread/merged into a payload without a truthy check at the call site.
 */
export const buildCollectionsPayload = collections => {
  const normalized = normalizeCollections(collections);

  if (!normalized.length) {
    return {};
  }

  return { collections: normalized };
};

/**
 * Builds the `collections.<type>=<id>` query string segment for the entity list GET,
 * per LEGO's dot-prefixed namespace convention (see LEGO Collections Framework doc):
 * different types are ANDed, and ids within a type are ORed by repeating the key.
 * A null/undefined id filters for entities with no item assigned for that type.
 */
export const buildCollectionsQueryString = collections =>
  usableCollections(collections)
    .flatMap(collection => collection.collection_item_ids.map(id => formatCollectionQueryParam(collection.type, id)))
    .join('&');

/**
 * The host's current workspace id: the first item of the `workspace_id` collection, or undefined when the host
 * didn't send one. Deliberately ignores the account's global workspace id.
 */
export const getCurrentWorkspaceId = collections => usableCollections(collections).find(collection => collection.type === 'workspace_id')?.collection_item_ids[0] ?? undefined;
